import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  CustomExperimentSpec,
  PanelRow,
  ActionBarSpec,
} from '@learnforge/demo-render/harness';
import { createReadout, type ReadoutHandle, type ReadoutRow } from '@learnforge/demo-render';
import { createSvgEl } from '@learnforge/demo-render';
import { niceCeil } from '@learnforge/demo-math/numeric';
import type { FinanceContext } from '../types';
import type { FinanceKey } from '../locales/keys';
import { createChart, type ChartHandle } from '../render/chart';
import { createLineSeries, type LineSeriesHandle } from '../render/line-series';
import { createCoinIcon } from '../render/coin';
import { realValue, realReturn, inflationAdjustedSeries } from '../finance/inflation';
import { compoundBalance } from '../finance/interest';
import { yearlySeries, type Series } from '../finance/series';
import { formatMoney, formatPercent, formatYears } from '../finance/format';

// ── State ─────────────────────────────────────────────────────────────────────

export interface InflationState {
  amount: number;
  inflation: number;
  years: number;
  savingsRate: number; // advanced: compare a savings account vs inflation
  compareSavings: boolean; // advanced
}

// ── Pure model (unit-tested) ──────────────────────────────────────────────────

export function inflationModel(state: InflationState): {
  series: Series[];
  readout: ReadoutRow[];
} {
  const { amount, inflation, years, savingsRate, compareSavings } = state;

  // Foundation: flat nominal cash vs declining real value
  const nominal = yearlySeries('nominal', () => amount, years);
  const real = yearlySeries('real', (t) => realValue(amount, inflation, t), years);

  const series: Series[] = [nominal, real];

  const realFinal = real.points.at(-1)?.value ?? amount;

  const readout: ReadoutRow[] = [
    {
      id: 'real-value',
      lhs: 'stat.real-value.label',
      relation: '=',
      rhs: formatMoney(realFinal),
    },
  ];

  if (compareSavings) {
    // Nominal savings balance (compound monthly)
    const savingsNominalPoints = yearlySeries(
      'savings',
      (t) => compoundBalance(amount, savingsRate, t, 12),
      years,
    );
    series.push(savingsNominalPoints);

    // Inflation-adjusted real savings
    const savingsRealSeries = inflationAdjustedSeries(
      { ...savingsNominalPoints, id: 'savings-real' },
      inflation,
    );
    series.push(savingsRealSeries);

    const rr = realReturn(savingsRate, inflation);
    const isBreakEven = savingsRate === inflation;
    const isLosingGround = savingsRate < inflation;

    readout.push({
      id: 'real-return',
      lhs: 'stat.real-return.label',
      relation: '=',
      rhs: formatPercent(rr, 2),
    });

    if (isBreakEven) {
      readout.push({
        id: 'break-even',
        lhs: 'stat.break-even.label',
        relation: '=',
        rhs: formatPercent(savingsRate, 1),
        emphasis: 'invariant',
      });
    } else if (isLosingGround) {
      readout.push({
        id: 'losing-ground',
        lhs: 'stat.losing-ground.label',
        relation: '=',
        rhs: formatPercent(rr, 2),
        emphasis: 'broken',
      });
    }
  }

  return { series, readout };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const CHART_OPTS = {
  width: 800,
  height: 460,
  padding: { top: 24, right: 32, bottom: 44, left: 80 },
  yTickCount: 5,
  xTickCount: 6,
} as const;

function defaultState(advanced: boolean): InflationState {
  return {
    amount: 10000,
    inflation: 0.03,
    years: 20,
    savingsRate: 0.015,
    compareSavings: advanced,
  };
}

const PRESETS = [
  { id: 'cash-under-mattress', labelKey: 'preset.cash-under-mattress.label' as FinanceKey },
  { id: 'low-savings-rate', labelKey: 'preset.low-savings-rate.label' as FinanceKey },
  { id: 'beats-inflation', labelKey: 'preset.beats-inflation.label' as FinanceKey },
];

function applyPresetLocal(state: InflationState, id: string): InflationState {
  switch (id) {
    case 'cash-under-mattress':
      return {
        ...state,
        amount: 10000,
        inflation: 0.04,
        years: 25,
        compareSavings: false,
      };
    case 'low-savings-rate':
      return {
        ...state,
        amount: 10000,
        inflation: 0.03,
        years: 20,
        savingsRate: 0.01,
        compareSavings: true,
      };
    case 'beats-inflation':
      return {
        ...state,
        amount: 10000,
        inflation: 0.03,
        years: 20,
        savingsRate: 0.05,
        compareSavings: true,
      };
    default:
      return state;
  }
}

function randomise(state: InflationState, prng: (max?: number) => number): InflationState {
  const amounts = [5000, 10000, 20000, 50000];
  const inflations = [0.02, 0.03, 0.04, 0.05, 0.06];
  const yearOptions = [10, 15, 20, 25, 30];
  const rates = [0.01, 0.015, 0.02, 0.03, 0.04, 0.05];
  return {
    ...state,
    amount: amounts[Math.floor(prng() * amounts.length)] ?? 10000,
    inflation: inflations[Math.floor(prng() * inflations.length)] ?? 0.03,
    years: yearOptions[Math.floor(prng() * yearOptions.length)] ?? 20,
    savingsRate: rates[Math.floor(prng() * rates.length)] ?? 0.015,
  };
}

/** CSS class to use for each series id. */
function classForSeries(id: string): string {
  switch (id) {
    case 'nominal':
      return 'fin-line-nominal';
    case 'real':
      return 'fin-line-real';
    case 'savings':
      return 'fin-line-savings';
    case 'savings-real':
      return 'fin-line-savings-real';
    default:
      return 'fin-line-a';
  }
}

// ── Spec factory ──────────────────────────────────────────────────────────────

function buildSpec(): CustomExperimentSpec<InflationState, FinanceKey, FinanceContext> {
  return {
    labelKey: 'experiment.inflation.label',
    demoId: 'finance',
    advancedKey: 'inflation',

    initialState: (ctx) => defaultState(ctx.advanced),

    applyPreset: (state, id) => applyPresetLocal(state, id),

    panel(state, ctx): PanelRow<FinanceKey, InflationState>[] {
      const rows: PanelRow<FinanceKey, InflationState>[] = [
        {
          kind: 'slider',
          id: 'amount',
          labelKey: 'param.start-amount.label',
          min: 1000,
          max: 100000,
          step: 1000,
          value: state.amount,
          format: formatMoney,
          onChange: (v) => ({ ...state, amount: v }),
        },
        {
          kind: 'slider',
          id: 'inflation',
          labelKey: 'param.inflation.label',
          min: 0.005,
          max: 0.15,
          step: 0.005,
          value: state.inflation,
          format: (v) => formatPercent(v, 1),
          onChange: (v) => ({ ...state, inflation: v }),
        },
        {
          kind: 'slider',
          id: 'years',
          labelKey: 'param.years.label',
          min: 1,
          max: 50,
          step: 1,
          value: state.years,
          format: formatYears,
          onChange: (v) => ({ ...state, years: v }),
        },
        {
          kind: 'advanced-toggle',
          labelKey: 'panel.advanced',
        },
      ];

      if (ctx.advanced) {
        rows.splice(3, 0, {
          kind: 'toggle',
          labelKey: 'param.compareSavings.label',
          value: state.compareSavings,
          onChange: (v) => ({ ...state, compareSavings: v }),
        });

        if (state.compareSavings) {
          rows.splice(4, 0, {
            kind: 'slider',
            id: 'savingsRate',
            labelKey: 'param.savingsRate.label',
            min: 0.001,
            max: 0.1,
            step: 0.001,
            value: state.savingsRate,
            format: (v) => formatPercent(v, 1),
            onChange: (v) => ({ ...state, savingsRate: v }),
          });
        }
      }

      return rows;
    },

    actions(_state, ctx): ActionBarSpec<FinanceKey, InflationState> {
      return {
        onReset: () => defaultState(ctx.advanced),
        onRandomise: (s) => randomise(s, ctx.prng),
        presets: PRESETS,
        onPreset: (s, id) => applyPresetLocal(s, id),
      };
    },

    applyShotState(state): InflationState {
      // Cash-eroding pose: flat nominal vs declining real
      return {
        ...state,
        amount: 10000,
        inflation: 0.04,
        years: 25,
        compareSavings: false,
      };
    },

    createRender(ctx, host, _controls) {
      // Build chart
      const chart: ChartHandle = createChart(host, {
        ...CHART_OPTS,
        formatX: (v) => formatYears(v),
        formatY: (v) => formatMoney(v),
      });

      // Coin icon at y-axis head
      const coinGroup = createCoinIcon(18);
      coinGroup.setAttribute(
        'transform',
        `translate(${CHART_OPTS.padding.left - 4}, ${CHART_OPTS.padding.top})`,
      );
      chart.svg.appendChild(coinGroup);

      // Line series layer
      const lines: LineSeriesHandle = createLineSeries(chart);

      // Legend group
      const legendGroup = createSvgEl('g', { class: 'fin-legend' });
      chart.svg.appendChild(legendGroup);

      // Readout — lives OUTSIDE ctx.root per the harness contract
      const readout: ReadoutHandle = createReadout(document.getElementById('readout') ?? host);

      function updateLegend(state: InflationState, seriesIds: string[]): void {
        while (legendGroup.firstChild) {
          legendGroup.removeChild(legendGroup.firstChild);
        }
        const { width, padding } = CHART_OPTS;
        // Right edge of the plot area, inset by a small margin
        const labelRightX = width - padding.right - 8;
        const swatchGap = 20; // pixels between swatch end and label left edge
        const swatchLen = 16;
        const legendY = padding.top + 16;

        const legendItems: { id: string; keyFn: () => string }[] = [
          { id: 'nominal', keyFn: () => ctx.i18n.t('series.nominal.label') },
          { id: 'real', keyFn: () => ctx.i18n.t('series.real.label') },
        ];

        if (state.compareSavings && seriesIds.includes('savings')) {
          legendItems.push({ id: 'savings', keyFn: () => ctx.i18n.t('series.savings.label') });
          legendItems.push({
            id: 'savings-real',
            keyFn: () => ctx.i18n.t('series.savings-real.label'),
          });
        }

        legendItems.forEach((item, i) => {
          const rowY = legendY + i * 18;
          // Swatch sits to the left of the label; label is right-anchored at labelRightX
          const swatchX2 = labelRightX - swatchGap;
          const swatchX1 = swatchX2 - swatchLen;

          const lineEl = createSvgEl('line', {
            class: `${classForSeries(item.id)} fin-legend-dot`,
            x1: swatchX1,
            y1: rowY,
            x2: swatchX2,
            y2: rowY,
          });
          legendGroup.appendChild(lineEl);

          const label = createSvgEl('text', {
            class: 'fin-legend',
            x: labelRightX,
            y: rowY,
            'text-anchor': 'end',
            'dominant-baseline': 'middle',
          });
          label.textContent = item.keyFn();
          legendGroup.appendChild(label);
        });
      }

      return {
        update(state: InflationState): void {
          const m = inflationModel(state);

          // Compute y-domain: max across all series
          let yMax = state.amount;
          for (const s of m.series) {
            for (const p of s.points) {
              if (p.value > yMax) yMax = p.value;
            }
          }

          chart.setScales({
            xDomain: [0, state.years],
            yDomain: [0, niceCeil(yMax * 1.05)],
          });

          // Known active series ids
          const ALL_SERIES = ['nominal', 'real', 'savings', 'savings-real'];
          const activeIds = new Set(m.series.map((s) => s.id));

          for (const s of m.series) {
            lines.setSeries(s.id, s.points, classForSeries(s.id));
          }

          // Clear any series that are no longer active
          for (const id of ALL_SERIES) {
            if (!activeIds.has(id)) {
              lines.clear(id);
            }
          }

          updateLegend(state, [...activeIds]);

          readout.update(m.readout.map((r) => ({ ...r, lhs: ctx.i18n.t(r.lhs as FinanceKey) })));
        },

        dispose(): void {
          readout.destroy();
          lines.destroy();
          chart.destroy();
        },
      };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
